"use client";

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LazyMotion, domAnimation, m } from "framer-motion";
import Link from "next/link";
import { ArrowUpRight, ImageOff, Play } from "lucide-react";
import { cn, formatAppSlug } from "@/lib/utils";
import { useAppAds } from "@/hooks/use-app-ads";
import { useThemeConfig } from "@/hooks/use-theme-config";
import { RailArrow } from "@/components/appstore/rail-arrow";
import { Skeleton } from "@/components/ui/skeleton";
import { ScreenLightbox, type LightboxItem } from "@/components/appstore/screen-lightbox";
import { PLATFORM_DOT } from "@/lib/ads/types";
import type { AdPlatform, AppAd, AppAdCreative } from "@/lib/ads/types";
import { PLATFORM_LABEL, availablePlatforms, platformCountsLine } from "@/lib/ads/platform-filter";
import { AppEmbedButton } from "@/components/embed/AppEmbedButton";
import { SITE_URL } from "@/lib/site-url";

interface AppAdsProps {
  appId: number;
  appName?: string | null;
  className?: string;
  /** 'embed' is /embed/ads: links open our site in a new tab, and an app with no ads says so. */
  variant?: "main" | "embed";
}

// How many ads the compact rail shows before the "See all" expander.
const PREVIEW_COUNT = 10;

// Where the source link actually lands per platform. Meta/Google are per-ad
// deep links; Apple's Ad Repository has no deep-linkable URLs, so its link
// opens the repository search page.
const LIBRARY_LABEL: Record<AdPlatform, string> = {
  meta: "View in Meta Ad Library",
  google: "View in Google Ads Transparency Center",
  apple: "Open Apple Ad Repository (search the app name there)",
};

// Same-origin fallback for creatives whose CDN a content blocker filters out
// (ad-library hosts sit on every blocklist). Cards try the direct URL first
// and retry through this; the lightbox uses it outright.
function proxyUrl(c: AppAdCreative): string {
  return `/api/app-detail/creative?id=${c.id}`;
}

function fmtDate(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fmtReach(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
}

// One-line serving context per platform: where the ad ran and in what form.
function metaLine(ad: AppAd): string | null {
  const parts: string[] = [];
  if (ad.platform === "meta") {
    if (ad.channels.length) parts.push(ad.channels.join(" · "));
  } else if (ad.platform === "apple") {
    if (ad.format) parts.push(ad.format);
    if (ad.placements.length) parts.push(ad.placements.join(", "));
    if (ad.countries.length) {
      const shown = ad.countries.slice(0, 3).join(", ");
      parts.push(ad.countries.length > 3 ? `${shown} +${ad.countries.length - 3}` : shown);
    }
  } else {
    if (ad.format) parts.push(ad.format);
    if (ad.regionCount) parts.push(`${ad.regionCount} region${ad.regionCount === 1 ? "" : "s"}`);
    if (ad.landingDomain) parts.push(ad.landingDomain);
  }
  if (ad.variantCount > 1) parts.push(`${ad.variantCount} variants`);
  return parts.length ? parts.join(" · ") : null;
}

// Flat list of every displayable creative, in ad order — powers the lightbox. Shared with
// the app's ad library page (components/ads/app-ad-library.tsx), which draws the same cards.
export function adLightboxItems(ads: readonly AppAd[]): { lbItems: LightboxItem[]; lbStartByAdId: Map<string, number> } {
  const items: LightboxItem[] = [];
  const startByAdId = new Map<string, number>();
  for (const ad of ads) {
    if (ad.creatives.length === 0) continue;
    startByAdId.set(ad.id, items.length);
    // A video ad opens on its video, over its poster frame; the poster stills are then redundant.
    const poster = ad.creatives.find((c) => c.role === "preview");
    const media: { src: string; type?: "video"; poster?: string }[] = ad.videoUrl
      ? [
          { src: ad.videoUrl, type: "video", poster: poster && proxyUrl(poster) },
          ...ad.creatives.filter((c) => c.role !== "preview").map((c) => ({ src: proxyUrl(c) })),
        ]
      : ad.creatives.map((c) => ({ src: proxyUrl(c) }));
    for (const c of media) {
      items.push({
        ...c,
        alt: ad.title || `${PLATFORM_LABEL[ad.platform]} ad creative`,
        caption: [
          PLATFORM_LABEL[ad.platform],
          ad.format,
          ad.title,
          ad.euReach && ad.euReach >= 1000 ? `EU reach ${fmtReach(ad.euReach)}` : null,
        ]
          .filter(Boolean)
          .join(" · "),
        href: ad.libraryUrl ?? undefined,
        hrefLabel:
          ad.platform === "apple"
            ? "Search Apple Ad Repository"
            : ad.platform === "meta"
              ? "View in Meta Ad Library"
              : "View in Ads Transparency Center",
        href2: ad.linkUrl ?? undefined,
        href2Label: ad.landingDomain ? `Visit ${ad.landingDomain}` : "Visit landing page",
      });
    }
  }
  return { lbItems: items, lbStartByAdId: startByAdId };
}

// Publisher ads sourced from the Meta / Google / Apple ad libraries.
// Non-blocking parallel slice: renders nothing until the lookup confirms we
// have ads for this app, so apps without collected ads cost zero layout.
// Progressive disclosure mirrors the screens sections above — a compact rail
// first, then a centered "See all" expander revealing the full grid.
export const AppAds = memo(function AppAds({ appId, appName, className, variant = "main" }: AppAdsProps) {
  const embed = variant === "embed";
  // Dev scale harness: `?adsim=12000` on the page URL simulates an N-entry ad
  // set (the API only honors it outside production).
  const [simulate] = useState(() =>
    typeof window === "undefined"
      ? 0
      : Number(new URLSearchParams(window.location.search).get("adsim")) || 0
  );
  // Which ad library the user is looking at. null is all of them, and it is
  // the only state the section starts in.
  const [platform, setPlatform] = useState<AdPlatform | null>(null);
  const { available, total, counts, ads, isLoading, hasMore } =
    useAppAds(appId, simulate || undefined, platform ?? undefined, "best");
  const { isTerminal, mounted } = useThemeConfig();
  const terminal = mounted && isTerminal;

  const [lbIndex, setLbIndex] = useState<number | null>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const updateScroll = useCallback(() => {
    const el = railRef.current;
    if (!el) return;
    setCanLeft(el.scrollLeft > 4);
    setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = railRef.current;
    if (!el) return;
    updateScroll();
    el.addEventListener("scroll", updateScroll, { passive: true });
    window.addEventListener("resize", updateScroll);
    return () => {
      el.removeEventListener("scroll", updateScroll);
      window.removeEventListener("resize", updateScroll);
    };
  }, [updateScroll, ads.length]);

  const scrollBy = useCallback((dir: "left" | "right") => {
    const el = railRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === "left" ? -el.clientWidth * 0.8 : el.clientWidth * 0.8, behavior: "smooth" });
  }, []);

  // Memoized against the stable `ads` array from React Query.
  const { lbItems, lbStartByAdId } = useMemo(() => adLightboxItems(ads), [ads]);

  // Render nothing while loading or when the publisher has no collected ads —
  // same self-hiding contract as the in-app screens section above.
  // A selected library always has at least one ad, because the control is
  // built from the counts. Keeping the section up while one is selected is
  // what stops a filter click from hiding the control that made it.
  // An embed has nothing around it to hide into, so it keeps a placeholder instead.
  if ((isLoading || !available || ads.length === 0) && platform === null) {
    if (!embed) return null;
    return isLoading ? (
      <div className={cn("flex gap-2.5 overflow-hidden", className)} aria-busy="true">
        {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} className="h-[275px] w-[200px] shrink-0 rounded-xl" />)}
      </div>
    ) : (
      <p className={cn("flex h-40 items-center justify-center text-sm text-muted-foreground", className)}>No ads collected for this app yet.</p>
    );
  }

  const preview = ads.slice(0, PREVIEW_COUNT);
  // `total` counts library entries and the cards are merged variants, so the
  // link shows only when there is a card past the preview or a page left.
  const libraryHref = `/advertisers/${formatAppSlug(appName || String(appId), appId)}`;
  // Inside an iframe a site link must leave the frame: our pages refuse to be framed outside /embed.
  const libraryLink = embed
    ? { href: `${SITE_URL}${libraryHref}?ref=embed`, target: "_blank", rel: "noopener noreferrer" }
    : { href: libraryHref };
  const showViewAll = ads.length > PREVIEW_COUNT || hasMore;

  const openAd = (ad: AppAd) => {
    const start = lbStartByAdId.get(ad.id);
    if (start !== undefined) setLbIndex(start);
  };

  // Whole-set library-entry counts from the server (not just loaded pages),
  // and they follow the selected library. Once every page is in, the line also
  // says how many cards those entries merged into, so "64 Apple" over one card
  // reads as one creative run 64 ways rather than as 63 missing ads.
  const countsLine = platformCountsLine(counts, platform);
  const mergedLine =
    !hasMore && !isLoading && ads.length > 0 && ads.length < total
      ? ` · ${ads.length.toLocaleString("en-US")} ${ads.length === 1 ? "creative" : "creatives"}`
      : "";

  // Only libraries this app actually has ads in, and no control at all when
  // there is only one of them — a filter with a single option is furniture.
  const platforms = availablePlatforms(counts);
  const pillBase = cn(
    "shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-[11.5px] font-semibold leading-none tabular-nums",
    terminal ? "font-mono" : "",
  );
  const pillIdle = terminal
    ? "text-emerald-300/70 ring-1 ring-emerald-500/25 hover:ring-emerald-500/50"
    : "text-muted-foreground ring-1 ring-border hover:text-foreground hover:bg-accent";
  const pillOn = terminal
    ? "text-emerald-200 ring-1 ring-emerald-400 bg-emerald-500/15"
    : "text-background bg-foreground ring-1 ring-foreground";

  const accentText = terminal ? "text-emerald-300" : "text-foreground";

  // Edge fade that only masks the side(s) with more content (same as the
  // screens rail above), so the first/last card is never clipped at rest.
  const fade = 36;
  const maskImage = `linear-gradient(to right, ${canLeft ? "transparent 0" : "black 0"}, black ${fade}px, black calc(100% - ${fade}px), ${canRight ? "transparent 100%" : "black 100%"})`;

  return (
    <LazyMotion features={domAnimation}>
      <m.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        id="ads"
        className={cn("group/ads relative w-full scroll-mt-16", className)}
        aria-label={`Ads run by the publisher of ${appName || "this app"}`}
      >
        {/* Header — total-count indicator right-aligned next to the title.
            The filter and the total need most of a 390px row between them, so
            below sm they take their own line rather than squeezing the title
            into a six-line ribbon. */}
        <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-3 mb-3">
          <div className="min-w-0">
            <h2
              className={cn(
                "text-xl font-bold tracking-tight leading-none",
                accentText,
                terminal && "font-mono uppercase tracking-wider"
              )}
            >
              Ads
            </h2>
            <p className={cn("text-[12px] mt-1 leading-snug text-muted-foreground", terminal && "font-mono")}>
              Running across the Meta, Google &amp; Apple ad libraries · {countsLine}{mergedLine}
              {" · "}
              <Link {...libraryLink} className={cn("underline-offset-2 hover:underline", accentText)}>
                Open ad library →
              </Link>
            </p>
          </div>
          <div className="flex w-full shrink-0 flex-wrap items-center gap-1.5 sm:w-auto sm:flex-nowrap">
            {/* Library filter. Rendered from the counts the server returns for
                THIS app, so a library it has no ads in is never offered and
                cannot open an empty panel. */}
            {platforms.length > 1 && (
              <div className="flex items-center gap-1.5" role="group" aria-label="Filter ads by library">
                <button
                  type="button"
                  onClick={() => setPlatform(null)}
                  aria-pressed={platform === null}
                  className={cn(pillBase, platform === null ? pillOn : pillIdle)}
                >
                  All
                </button>
                {platforms.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPlatform(p)}
                    aria-pressed={platform === p}
                    aria-label={`${PLATFORM_LABEL[p]} — ${counts[p].toLocaleString("en-US")} ads`}
                    className={cn(pillBase, platform === p ? pillOn : pillIdle)}
                  >
                    {PLATFORM_LABEL[p]}
                  </button>
                ))}
              </div>
            )}
            <span
              className={cn(
                pillBase,
                terminal
                  ? "text-emerald-300 ring-1 ring-emerald-500/40"
                  : "text-foreground/80 ring-1 ring-border bg-muted/40"
              )}
              aria-label={`${total.toLocaleString("en-US")} ads collected for this app`}
            >
              {total.toLocaleString("en-US")} ads
            </span>
            {!embed && <AppEmbedButton type="ads" appId={appId} />}
          </div>
        </div>

        {/* Compact rail + edge arrows */}
        <div className="relative">
          <RailArrow
            dir="left"
            visible={canLeft}
            terminal={terminal}
            onClick={() => scrollBy("left")}
            groupHoverClass="group-hover/ads:opacity-100"
          />
          <RailArrow
            dir="right"
            visible={canRight}
            terminal={terminal}
            onClick={() => scrollBy("right")}
            groupHoverClass="group-hover/ads:opacity-100"
          />

          <div
            ref={railRef}
            className="flex gap-2.5 overflow-x-auto overflow-y-hidden hide-scrollbar snap-x scroll-smooth py-1"
            style={{ WebkitMaskImage: maskImage, maskImage }}
          >
            {preview.map((ad) => (
              <AdCard key={ad.id} ad={ad} terminal={terminal} onOpen={openAd} inRail />
            ))}
          </div>
        </div>

        {/* View all opens the app's own ad library page */}
        {showViewAll && (
          <div className="mt-3">
            <div className="flex justify-center">
              <Link
                {...libraryLink}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm font-medium transition-all",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                  terminal
                    ? "text-emerald-300 ring-1 ring-emerald-500/40 hover:bg-emerald-500/10 focus-visible:ring-emerald-500"
                    : "text-foreground/80 ring-1 ring-border hover:bg-accent hover:text-foreground focus-visible:ring-sky-500"
                )}
              >
                {`View all ${total.toLocaleString("en-US")} ads`}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        )}

        <ScreenLightbox items={lbItems} index={lbIndex} onIndex={setLbIndex} onClose={() => setLbIndex(null)} />
      </m.section>
    </LazyMotion>
  );
});

/* ── Single ad card ───────────────────────────────────────────────────── */

export const AdCard = memo(function AdCard({
  ad,
  terminal,
  onOpen,
  inRail = false,
}: {
  ad: AppAd;
  terminal: boolean;
  onOpen: (ad: AppAd) => void;
  inRail?: boolean;
}) {
  // Image source ladder: direct CDN URL → same-origin proxy → placeholder.
  const [imgSource, setImgSource] = useState<"direct" | "proxy" | "failed">("direct");
  const imgFailed = imgSource === "failed";
  const [imgLoaded, setImgLoaded] = useState(false);
  const showImage = ad.thumb && !imgFailed;
  const openable = ad.creatives.length > 0 && !imgFailed;
  const date = fmtDate(ad.lastSeenAt ?? ad.scrapedAt);
  const label = ad.title || ad.body || ad.ctaText;
  const context = metaLine(ad);
  // Google archives text/display ads as full screenshots on white — cropping
  // them beheads the copy, so contain-fit those; photo creatives stay covered.
  const containFit = ad.platform === "google";

  const media = (
    <>
      {showImage ? (
        // oxlint-disable-next-line react-doctor/nextjs-no-img-element
        <>
          {!imgLoaded && <Skeleton className="absolute inset-0 rounded-none motion-reduce:animate-none" />}
          <img
            ref={(img) => { if (img?.complete && img.naturalWidth > 0) setImgLoaded(true); }}
            src={imgSource === "direct" ? ad.thumb!.url : proxyUrl(ad.thumb!)}
            alt={label || `${PLATFORM_LABEL[ad.platform]} ad creative`}
            loading="lazy"
            draggable={false}
            className={cn(
              "h-full w-full select-none transition-opacity duration-300 motion-reduce:transition-none",
              imgLoaded ? "opacity-100" : "opacity-0",
              // pt-8 keeps the platform chip off the ad copy at the top of the screenshot.
              containFit ? "object-contain px-2 pb-2 pt-8" : "object-cover object-top"
            )}
            onLoad={() => setImgLoaded(true)}
            onError={() => setImgSource((s) => (s === "direct" ? "proxy" : "failed"))}
          />
        </>
      ) : (
        <div className="h-full w-full flex flex-col items-center justify-center gap-1.5 text-muted-foreground">
          <ImageOff className="h-5 w-5 opacity-50" aria-hidden />
          <span className="text-[10px]">
            {imgFailed ? "Creative unavailable" : ad.format ? `${ad.format} · no preview` : "No preview"}
          </span>
        </div>
      )}

      {/* Platform chip */}
      <span className="pointer-events-none absolute top-2 left-2 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/65 px-2 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
        <span className={cn("h-1.5 w-1.5 rounded-full", PLATFORM_DOT[ad.platform])} aria-hidden />
        {PLATFORM_LABEL[ad.platform]}
      </span>

      {/* Video + multi-creative markers */}
      {ad.hasVideo && (
        <span
          className="pointer-events-none absolute top-2 right-2 z-10 grid place-items-center h-6 w-6 rounded-full bg-black/65 text-white backdrop-blur-sm"
          title="Video ad"
        >
          <Play className="h-3 w-3 fill-current" aria-hidden />
        </span>
      )}
      {ad.creatives.length > 1 && (
        <span className="pointer-events-none absolute bottom-2 right-2 z-10 rounded-md bg-black/65 px-1.5 py-0.5 text-[10px] font-medium text-white backdrop-blur-sm">
          +{ad.creatives.length - 1}
        </span>
      )}
    </>
  );

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden rounded-xl border transition-shadow duration-300 shadow-sm hover:shadow-md",
        "bg-sidebar-lighter",
        terminal
          ? "border-emerald-500/40"
          : "border-black/[0.04] dark:border-white/[0.06]",
        inRail && "snap-start shrink-0 w-[200px]"
      )}
    >
      {/* Media — clicking expands the ad's creatives in the lightbox */}
      {openable ? (
        <button
          type="button"
          onClick={() => onOpen(ad)}
          aria-label={`Expand ${PLATFORM_LABEL[ad.platform]} ad${label ? `: ${label}` : ""}`}
          className={cn(
            "relative h-[180px] w-full shrink-0 overflow-hidden cursor-pointer outline-none",
            containFit && showImage ? "bg-white" : "bg-muted",
            "focus-visible:ring-2 focus-visible:ring-inset",
            terminal ? "focus-visible:ring-emerald-500" : "focus-visible:ring-sky-500"
          )}
        >
          {media}
        </button>
      ) : (
        <div
          className={cn(
            "relative h-[180px] w-full shrink-0 overflow-hidden",
            containFit && showImage ? "bg-white" : "bg-muted"
          )}
        >
          {media}
        </div>
      )}

      {/* Copy + serving context + status */}
      <div className="flex flex-col flex-1 gap-1 p-2.5">
        <p
          className={cn(
            "text-[12px] font-medium leading-snug line-clamp-2 min-h-[2.1em]",
            terminal ? "text-emerald-100/90" : "text-foreground/90"
          )}
        >
          {label || `${PLATFORM_LABEL[ad.platform]} ad`}
        </p>
        {context && (
          <p className="text-[10.5px] leading-tight text-muted-foreground truncate" title={context}>
            {context}
          </p>
        )}
        <div className="mt-auto pt-1 flex items-center justify-between gap-2 text-[10.5px] text-muted-foreground">
          <span
            className="inline-flex items-center gap-1.5 truncate"
            title={ad.isActive ? `Still running as of ${date ?? "last check"}` : `Last seen ${date ?? "—"}`}
          >
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full shrink-0",
                ad.isActive ? "bg-emerald-500" : "bg-muted-foreground/40"
              )}
              aria-hidden
            />
            {ad.isActive ? "Active" : "Inactive"}
            {date ? ` · ${date}` : ""}
          </span>
          {ad.euReach && ad.euReach >= 1000 ? (
            <span
              className={cn(
                "shrink-0 rounded-md px-1.5 py-0.5 text-[9.5px] font-medium",
                terminal
                  ? "bg-emerald-500/15 text-emerald-300"
                  : "bg-sky-500/10 text-sky-600 dark:text-sky-400"
              )}
              title={`Reached ${ad.euReach.toLocaleString("en-US")} people in the EU (DSA transparency data)`}
            >
              EU {fmtReach(ad.euReach)}
            </span>
          ) : ad.ctaText && label !== ad.ctaText ? (
            <span className="shrink-0 truncate max-w-[40%]" title={ad.ctaText}>
              {ad.ctaText}
            </span>
          ) : null}
          {ad.libraryUrl && (
            <a
              href={ad.libraryUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={LIBRARY_LABEL[ad.platform]}
              title={LIBRARY_LABEL[ad.platform]}
              onClick={(e) => e.stopPropagation()}
              className={cn(
                "shrink-0 -m-1 grid place-items-center h-6 w-6 rounded-md transition-colors",
                "text-muted-foreground/70 hover:text-foreground hover:bg-accent",
                "focus-visible:outline-none focus-visible:ring-2",
                terminal ? "focus-visible:ring-emerald-500" : "focus-visible:ring-sky-500"
              )}
            >
              <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            </a>
          )}
        </div>
      </div>
    </div>
  );
});

export default AppAds;
